/**
 * FikoStrateji3DViewer — Strateji/Geometri sekmesi için ŞEMATİK interaktif 3D (CATIA tarzı).
 * Gerçek parça DEĞİL; operasyona göre üretilen basit cep/blok şeması:
 *  - Kontur çizgisi → tıkla → kontur seçim modu
 *  - Limit düzlemleri (üst/alt) → tıkla → limit (düzlem) seçim
 *  - Ada işaretleri → tıkla → ada seçim
 *  - Offset etiketleri (üst/kontür/taban/check) → ÇİFT-TIK → değer düzenle
 * Makro 3D ile aynı gömme tekniği (FikoMini3DDialogBase = tam FikoThreeJsComponent).
 */
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewContainerRef, EnvironmentInjector, NgZone, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { FikoCamMini3DBase } from './fiko-cam-mini-3d.base';
import { ICamOperasyon, AXIAL_TIPLER } from './fiko-cam.types';

export type StratejiRol = 'kontur' | 'ada' | 'limitUst' | 'limitAlt' | 'nokta' | 'toolaxis' | 'limit1' | 'limit2';
export type StratejiOffset = 'ofsetUst' | 'ofsetKontur' | 'ofsetTaban' | 'ofsetCheck';

@Component({
  selector: 'fiko-strateji-3d',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #viewerContainer class="st3d-vc" [style.height.px]="viewerYukseklik"></div>`,
  styles: [`
    .st3d-vc{position:relative;width:100%;overflow:hidden;border-radius:6px;border:1px solid rgba(0,200,255,.2);background:#0a1622;}
    .st3d-vc ::ng-deep canvas{display:block;width:100%!important;height:100%!important;}
  `]
})
export class FikoStrateji3DViewer extends FikoCamMini3DBase implements OnChanges {
  @ViewChild('viewerContainer', { static: true }) viewerContainerRef!: ElementRef<HTMLDivElement>;

  @Input() op!: ICamOperasyon;
  @Output() rolSec = new EventEmitter<StratejiRol>();
  @Output() offsetDuzenle = new EventEmitter<StratejiOffset>();

  private grup: THREE.Group | null = null;
  private _ext = 40;
  private downH: ((e: PointerEvent) => void) | null = null;
  private dblH: ((e: MouseEvent) => void) | null = null;
  private hoverH: ((e: PointerEvent) => void) | null = null;
  private _hoverRol: string | null = null;
  private _downXY = { x: 0, y: 0 }; private _moved = false; private _downRol: StratejiRol | null = null;
  private _rc = new THREE.Raycaster(); private _v2 = new THREE.Vector2(); private _ak = new THREE.Color(0xffffff);

  constructor(cd: ChangeDetectorRef, injector: Injector, viewContainerRef: ViewContainerRef, environmentInjector: EnvironmentInjector, ngZone: NgZone) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 232; this.viewerYukseklik = 200;
  }
  override sahneHazir(): void {
    if (!this.sahne) return;
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && !this.downH) {
      this.downH = (e) => this.basla(e); cv.addEventListener('pointerdown', this.downH, true);
      this.dblH = (e) => this.ciftTik(e); cv.addEventListener('dblclick', this.dblH);
      this.hoverH = (e) => this.hover(e); cv.addEventListener('pointermove', this.hoverH);
    }
    this.ciz(); setTimeout(() => this.cercevele(), 80);
  }
  ngOnChanges(): void { if (this.hazirMi) this.ciz(); }

  /** Operasyon konturu (yoksa varsayılan L-profil şema — ayırt edici). */
  private konturAl(): { x: number; y: number }[] {
    const k = this.op?.geometri?.konturlar?.[0];
    if (k && k.length >= 3) return k;
    return [{ x: -26, y: -20 }, { x: 26, y: -20 }, { x: 26, y: 2 }, { x: -2, y: 2 }, { x: -2, y: 20 }, { x: -26, y: 20 }];   // L şekli (henüz seçilmemiş)
  }
  private ciz(): void {
    const s: any = this.sahne; if (!s) return;
    if (this.grup) { s.remove(this.grup); this.grup.traverse((o: any) => { o.geometry?.dispose?.(); if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m: any) => { m.map?.dispose?.(); m.dispose?.(); }); }); }
    const g = new THREE.Group(); g.name = 'StratejiSema'; this.grup = g;
    const secilmis = !!(this.op?.geometri?.konturlar?.[0]?.length);
    const kontur = this.konturAl();
    let minx = 1e9, maxx = -1e9, miny = 1e9, maxy = -1e9;
    for (const p of kontur) { minx = Math.min(minx, p.x); maxx = Math.max(maxx, p.x); miny = Math.min(miny, p.y); maxy = Math.max(maxy, p.y); }
    const ext = this._ext = Math.max(10, maxx - minx, maxy - miny);
    const ustZ = this.op?.geometri?.ustZ ?? 0;
    const derin = Math.max(4, ext * 0.35); const altZ = this.op?.geometri?.altZ ?? (ustZ - derin);
    const cap = (Math.max(this.op?.geometri?.cap || 0, 6)); const margin = Math.max(ext * 0.28, 10);

    // 1) MALZEME BLOĞU (stok) — OPAK görünüm için sadece tel-kafes (dolgu içi gizlemesin)
    const blkGeo = new THREE.BoxGeometry((maxx - minx) + margin * 2, (maxy - miny) + margin * 2, (ustZ - altZ) + ext * 0.12);
    const blkKenar = new THREE.LineSegments(new THREE.EdgesGeometry(blkGeo), new THREE.LineBasicMaterial({ color: 0xd8b878 })); blkKenar.position.set((minx + maxx) / 2, (miny + maxy) / 2, (ustZ + altZ) / 2); (blkKenar as any).raycast = () => { }; g.add(blkKenar); blkGeo.dispose();

    // 2-3) KOMUTA GÖRE FARKLI HERO ŞEKİL (ayırt edici) + tıklanabilir roller
    const kat = this.sekilKategori(); const kontC = secilmis ? 0xffcc33 : 0x33d6ff; const cx0 = (minx + maxx) / 2, cy0 = (miny + maxy) / 2;
    if (kat === 'cep') {                       // CEP: içi boş kapalı kavite (yeşil) + kontur çizgisi + ada
      const sekil = new THREE.Shape(kontur.map(p => new THREE.Vector2(p.x, p.y)));
      const cep = new THREE.Mesh(new THREE.ExtrudeGeometry(sekil, { depth: ustZ - altZ, bevelEnabled: false }), new THREE.MeshBasicMaterial({ color: 0x14692f, side: THREE.DoubleSide }));
      cep.position.z = altZ; g.add(cep); this.konturCizgi(g, kontur, ustZ, ext, kontC, true);
    } else if (kat === 'yuzey') {              // YÜZEY TARAMA: üstte ince talaş katmanı (skim) — sığ
      const h = Math.max(1.5, ext * 0.06);
      const slab = new THREE.Mesh(new THREE.BoxGeometry(maxx - minx, maxy - miny, h), new THREE.MeshBasicMaterial({ color: 0x14692f }));
      slab.position.set(cx0, cy0, ustZ - h / 2); g.add(slab); this.konturCizgi(g, kontur, ustZ, ext, kontC, true);
    } else if (kat === 'profil') {             // PROFİL/EĞRİ: kapalı kavite YOK; kontur boyunca duvar şeridi (açık iz)
      this.duvarSeridi(g, kontur, ustZ, altZ); this.konturCizgi(g, kontur, ustZ, ext, kontC, this.op?.tip !== 'curveFollow');
      // CATIA Limit 1 / Limit 2 — işlemenin başlangıç/bitiş sınırı (tıkla → 3D'de sınır noktası seç)
      const gg2 = this.op?.geometri || {} as any;
      const p1 = gg2.baslangic || kontur[0]; const p2 = gg2.bitis || kontur[kontur.length - 1];
      const limitNok = (p: any, rol: StratejiRol, renk: number, lbl: string) => {
        const d = new THREE.Mesh(new THREE.SphereGeometry(ext * 0.06, 14, 10), new THREE.MeshBasicMaterial({ color: renk })); d.position.set(p.x, p.y, ustZ); d.userData['rol'] = rol; d.name = 'rol' + rol; g.add(d);
        const et = this.etiketSprite(lbl, renk, ext); et.position.set(p.x, p.y, ustZ + ext * 0.16); et.userData['rol'] = rol; et.scale.multiplyScalar(0.72); g.add(et);
      };
      limitNok(p1, 'limit1', 0x22d3ee, 'Limit 1'); limitNok(p2, 'limit2', 0xfb7185, 'Limit 2');
    } else if (kat === 'dairesel') {           // DAİRESEL FREZE: yuvarlak delik/boru kavitesi
      const R = Math.max(4, Math.min(maxx - minx, maxy - miny) / 2);
      const cyl = new THREE.Mesh(new THREE.CylinderGeometry(R, R, ustZ - altZ, 32), new THREE.MeshBasicMaterial({ color: 0x14692f }));
      cyl.rotation.x = Math.PI / 2; cyl.position.set(cx0, cy0, (ustZ + altZ) / 2); g.add(cyl);
      const cline = new THREE.Mesh(new THREE.TorusGeometry(R, ext * 0.02, 8, 40), new THREE.MeshBasicMaterial({ color: kontC })); cline.position.set(cx0, cy0, ustZ); cline.userData['rol'] = 'kontur'; cline.name = 'rolKontur'; g.add(cline);
    } else if (kat === 'delik') {              // DELME/EKSENEL: dikey delikler (nokta seçimi)
      const nk = (this.op?.geometri?.noktalar?.length ? this.op.geometri.noktalar : [{ x: minx + (maxx - minx) * .3, y: cy0 }, { x: minx + (maxx - minx) * .7, y: cy0 }]) as any[];
      const r = (this.op?.geometri?.cap || cap) / 2;
      for (const p of nk) {
        const hole = new THREE.Mesh(new THREE.CylinderGeometry(r, r, ustZ - altZ, 20), new THREE.MeshBasicMaterial({ color: 0x14692f }));
        hole.rotation.x = Math.PI / 2; hole.position.set(p.x, p.y, (ustZ + altZ) / 2); hole.userData['rol'] = 'nokta'; hole.name = 'rolNokta'; g.add(hole);
        const halo = new THREE.Mesh(new THREE.TorusGeometry(r, ext * 0.018, 8, 24), new THREE.MeshBasicMaterial({ color: kontC })); halo.position.set(p.x, p.y, ustZ); halo.userData['rol'] = 'nokta'; halo.name = 'rolNokta'; g.add(halo);
      }
    } else {                                   // NOKTA-NOKTA: nokta işaretleri
      const nk = (this.op?.geometri?.noktalar?.length ? this.op.geometri.noktalar : [{ x: cx0, y: cy0 }]) as any[];
      for (const p of nk) { const d = new THREE.Mesh(new THREE.SphereGeometry(ext * 0.05, 16, 12), new THREE.MeshBasicMaterial({ color: kontC })); d.position.set(p.x, p.y, ustZ); d.userData['rol'] = 'nokta'; d.name = 'rolNokta'; g.add(d); }
    }

    // 3b) TAKIM EKSENİ (yön) oku — TIKLA → Tool Axis dialog. Yön op.takimYonu'dan (yoksa +Z).
    const ty = (this.op as any)?.takimYonu?.yon; const yon = new THREE.Vector3(ty?.x ?? 0, ty?.y ?? 0, ty?.z ?? 1); if (yon.lengthSq() < 1e-6) yon.set(0, 0, 1); yon.normalize();
    const taL = ext * 0.6, taBas = new THREE.Vector3(cx0, cy0, ustZ);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(ext * 0.022, ext * 0.022, taL, 8), new THREE.MeshBasicMaterial({ color: 0xf472b6 }));
    shaft.position.copy(taBas).addScaledVector(yon, taL / 2); shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), yon); shaft.userData['rol'] = 'toolaxis'; shaft.name = 'rolToolaxis'; g.add(shaft);
    const cone = new THREE.Mesh(new THREE.ConeGeometry(ext * 0.055, ext * 0.14, 14), new THREE.MeshBasicMaterial({ color: 0xf472b6 }));
    cone.position.copy(taBas).addScaledVector(yon, taL); cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), yon); cone.userData['rol'] = 'toolaxis'; cone.name = 'rolToolaxis'; g.add(cone);
    const taEt = this.etiketSprite('⊥ ' + this.eksenAd(yon), 0xf9a8d4, ext); taEt.position.copy(taBas).addScaledVector(yon, taL + ext * 0.18); taEt.userData['rol'] = 'toolaxis'; g.add(taEt);

    // 4) LİMİT DÜZLEMLERİ — opak görünüm için içi gizlemeyen KENAR-ÇERÇEVE (tıklanabilir). Üst (ustZ) + alt (altZ).
    const limitDuzlem = (z: number, rol: StratejiRol, renk: number) => {
      const pg = new THREE.PlaneGeometry((maxx - minx) * 0.62, (maxy - miny) * 0.62);   // daha ufak çerçeve
      const eg = new THREE.LineSegments(new THREE.EdgesGeometry(pg), new THREE.LineBasicMaterial({ color: renk })); eg.position.set((minx + maxx) / 2, (miny + maxy) / 2, z); eg.userData['rol'] = rol; eg.name = 'rol' + rol; g.add(eg); pg.dispose();
      // köşe küçük küreler: limit'i görünür + kolay tıklanır kıl
      for (const sx of [-1, 1]) for (const sy of [-1, 1]) { const d = new THREE.Mesh(new THREE.SphereGeometry(ext * 0.035, 10, 8), new THREE.MeshBasicMaterial({ color: renk })); d.position.set((minx + maxx) / 2 + sx * (maxx - minx) * 0.31, (miny + maxy) / 2 + sy * (maxy - miny) * 0.31, z); d.userData['rol'] = rol; d.name = 'rol' + rol; g.add(d); }
      const et = this.etiketSprite(rol === 'limitUst' ? 'Üst Limit' : 'Alt Limit', renk, ext); et.position.set((minx + maxx) / 2, (miny + maxy) / 2 + (maxy - miny) * 0.31, z); et.userData['rol'] = rol; et.scale.multiplyScalar(0.7); g.add(et);
    };
    limitDuzlem(ustZ + ext * 0.18, 'limitUst', 0x38bdf8);   // solidden Z'de biraz uzak (üstte)
    limitDuzlem(altZ - ext * 0.18, 'limitAlt', 0xf472b6);   // solidden biraz uzak (altta)

    // 5) ADALAR — kırmızı işaret; TIKLA → ada seç (yoksa şema: ortada soluk bir aday)
    const adalar = this.op?.geometri?.adalar || [];
    if (adalar.length) {
      for (const ada of adalar) {
        let ax = 0, ay = 0; for (const p of ada) { ax += p.x; ay += p.y; } ax /= ada.length; ay /= ada.length;
        const d = new THREE.Mesh(new THREE.CylinderGeometry(ext * 0.07, ext * 0.07, (ustZ - altZ) * 0.6, 16), new THREE.MeshBasicMaterial({ color: 0xe23b3b }));
        d.rotation.x = Math.PI / 2; d.position.set(ax, ay, (ustZ + altZ) / 2); d.userData['rol'] = 'ada'; d.name = 'rolAda'; g.add(d);
      }
    } else if (this.op?.tip === 'pocketing') {
      const d = new THREE.Mesh(new THREE.CylinderGeometry(ext * 0.06, ext * 0.06, ext * 0.1, 16), new THREE.MeshBasicMaterial({ color: 0xe23b3b, transparent: true, opacity: 0.4 }));
      d.rotation.x = Math.PI / 2; d.position.set((minx + maxx) / 2, (miny + maxy) / 2, ustZ); d.userData['rol'] = 'ada'; d.name = 'rolAda'; g.add(d);
    }

    // 6) OFFSET ETİKETLERİ — ÇİFT-TIK → düzenle
    const gg = this.op?.geometri || {} as any; const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2; const dış = margin + ext * 0.5;
    const off: { f: StratejiOffset; t: string; x: number; y: number; z: number }[] = [
      { f: 'ofsetUst', t: 'Üst ' + (gg.ofsetUst ?? 0), x: cx, y: maxy + dış, z: ustZ },
      { f: 'ofsetKontur', t: 'Kontür ' + (gg.ofsetKontur ?? 0), x: maxx + dış, y: cy, z: (ustZ + altZ) / 2 },
      { f: 'ofsetTaban', t: 'Taban ' + (gg.ofsetTaban ?? 0), x: cx, y: miny - dış, z: altZ },
      { f: 'ofsetCheck', t: 'Check ' + (gg.ofsetCheck ?? 0), x: minx - dış, y: cy, z: ustZ },
    ];
    for (const o of off) {
      const sp = this.etiketSprite(o.t, 0x9fe8ff, ext); sp.position.set(o.x, o.y, o.z); sp.userData['offset'] = o.f; sp.name = 'off'; g.add(sp);
      const lg = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(cx, cy, o.z), new THREE.Vector3(o.x, o.y, o.z)]);
      const ll = new THREE.Line(lg, new THREE.LineBasicMaterial({ color: 0x4a7a92 })); (ll as any).raycast = () => { }; g.add(ll);
    }
    s.add(g); if (typeof s.anaGosterim?.ciz === 'function') s.anaGosterim.ciz();
  }

  /** Komut tipi → şema kategorisi (ayırt edici şekil). */
  private sekilKategori(): 'cep' | 'yuzey' | 'profil' | 'dairesel' | 'delik' | 'nokta' {
    const t = this.op?.tip as any;
    if (AXIAL_TIPLER.includes(t)) return 'delik';
    if (t === 'facing') return 'yuzey';
    if (t === 'pocketing') return 'cep';
    if (t === 'contouring' || t === 'curveFollow') return 'profil';
    if (t === 'circularMill') return 'dairesel';
    if (t === 'pointToPoint') return 'nokta';
    return 'cep';
  }
  /** Yön vektörü → eksen adı (etiket). */
  private eksenAd(y: THREE.Vector3): string { if (Math.abs(y.z) > 0.99) return 'Z'; if (Math.abs(y.x) > 0.99) return 'X'; if (Math.abs(y.y) > 0.99) return 'Y'; return 'Özel'; }
  /** Kontur çizgisi + kalın görünmez tıklama tüpleri (rol='kontur'). */
  private konturCizgi(g: THREE.Group, kontur: { x: number; y: number }[], ustZ: number, ext: number, renk: number, kapali: boolean): void {
    const pts = kontur.map(p => new THREE.Vector3(p.x, p.y, ustZ)); if (kapali) pts.push(pts[0].clone());
    const ln = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: renk })); ln.userData['rol'] = 'kontur'; ln.name = 'rolKontur'; g.add(ln);
    const n = kapali ? kontur.length : kontur.length - 1;
    for (let i = 0; i < n; i++) {
      const a = kontur[i], b = kontur[(i + 1) % kontur.length]; const len = Math.hypot(b.x - a.x, b.y - a.y); if (len < 1e-3) continue;
      const seg = new THREE.Mesh(new THREE.CylinderGeometry(ext * 0.025, ext * 0.025, len, 6), new THREE.MeshBasicMaterial({ color: renk }));
      seg.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, ustZ); seg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(b.x - a.x, b.y - a.y, 0).normalize());
      seg.userData['rol'] = 'kontur'; seg.name = 'rolKontur'; g.add(seg);
    }
  }
  /** Profil için kontur boyunca dikey duvar şeridi (kapalı kavite değil — açık iz). */
  private duvarSeridi(g: THREE.Group, kontur: { x: number; y: number }[], ustZ: number, altZ: number): void {
    const pos: number[] = [];
    for (let i = 0; i < kontur.length; i++) {
      const a = kontur[i], b = kontur[(i + 1) % kontur.length];
      pos.push(a.x, a.y, ustZ, b.x, b.y, ustZ, a.x, a.y, altZ, b.x, b.y, ustZ, b.x, b.y, altZ, a.x, a.y, altZ);
    }
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); geo.computeVertexNormals();
    const wall = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x14692f, side: THREE.DoubleSide })); (wall as any).raycast = () => { }; g.add(wall);
  }
  private etiketSprite(metin: string, renk: number, ext: number): THREE.Sprite {
    const W = 256, H = 64, cv = document.createElement('canvas'); cv.width = W; cv.height = H; const ctx = cv.getContext('2d')!;
    const hex = '#' + renk.toString(16).padStart(6, '0');
    ctx.fillStyle = 'rgba(8,18,30,.92)'; this.yuvarlak(ctx, 4, 4, W - 8, H - 8, 12); ctx.fill();
    ctx.strokeStyle = hex; ctx.lineWidth = 3; this.yuvarlak(ctx, 4, 4, W - 8, H - 8, 12); ctx.stroke();
    ctx.fillStyle = '#eaf6ff'; ctx.font = 'bold 30px system-ui,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(metin, W / 2, H / 2 + 1);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    const sc = Math.max(3, ext * 0.14); sp.scale.set(sc * (W / H), sc, 1); sp.renderOrder = 999; return sp;
  }
  private yuvarlak(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }

  private cercevele(): void {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; if (!kam) return;
    const d = Math.max(20, this._ext * 3);
    kam.position.set(d, -d, d); if (kam.lookAt) kam.lookAt(0, 0, 0);
    if (kam.isOrthographicCamera) { kam.zoom = Math.max(0.8, 42 / Math.max(1, this._ext)); kam.updateProjectionMatrix?.(); }
    if (ag.aktifKontrol?.target) { ag.aktifKontrol.target.set(0, 0, 0); ag.aktifKontrol.update?.(); }
    ag.ciz?.();
  }

  private isabet(e: MouseEvent): { rol?: StratejiRol; offset?: StratejiOffset } | null {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; const cv = ag?.canvas?.nativeElement; if (!kam || !cv || !this.grup) return null;
    const r = cv.getBoundingClientRect();
    this._v2.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this._rc.setFromCamera(this._v2, kam);
    if (this._rc.params.Line) this._rc.params.Line.threshold = Math.max(1.5, this._ext * 0.12);   // limit/kontur çizgileri kolay tıklanabilir
    const its = this._rc.intersectObjects(this.grup.children, true);
    const hit = its.find(i => (i.object as any).userData?.['rol'] || (i.object as any).userData?.['offset']);
    if (!hit) return null;
    const ud = (hit.object as any).userData;
    return { rol: ud['rol'], offset: ud['offset'] };
  }
  /** TEK-tık seçim (offset değil): rol elemanına tıkla → seçim modu. Sürükle (orbit) ayırt edilir. */
  private basla(e: PointerEvent): void {
    if (e.button !== 0) return;   // SADECE SOL tuş seçer; orta/sağ → kamera (orbit/pan) serbest
    const h = this.isabet(e); if (!h || !h.rol) return;   // boşluk/offset → orbit'e bırak
    this._downRol = h.rol; this._downXY = { x: e.clientX, y: e.clientY }; this._moved = false;
    const mv = (ev: PointerEvent) => { if (Math.hypot(ev.clientX - this._downXY.x, ev.clientY - this._downXY.y) > 4) this._moved = true; };
    const up = () => {
      window.removeEventListener('pointermove', mv, true); window.removeEventListener('pointerup', up, true);
      if (this._downRol && !this._moved) { const r = this._downRol; this.ngZone.run(() => this.rolSec.emit(r)); }
      this._downRol = null;
    };
    window.addEventListener('pointermove', mv, true); window.addEventListener('pointerup', up, true);
  }
  /** ÇİFT-tık offset etiketine → düzenle. */
  private ciftTik(e: MouseEvent): void { const h = this.isabet(e); if (h?.offset) { e.stopPropagation(); const o = h.offset; this.ngZone.run(() => this.offsetDuzenle.emit(o)); } }
  /** Hover: imleç altındaki rol'ü vurgula (highlight) + cursor. */
  private hover(e: PointerEvent): void {
    if (this._downRol) return;   // sürükleme/seçim sırasında karışma
    const h = this.isabet(e); const rol = h?.rol || (h?.offset ? 'off' : null);
    const cv = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement; if (cv) cv.style.cursor = rol ? 'pointer' : '';
    if (rol === this._hoverRol) return; this._hoverRol = rol; this.vurgula(rol);
  }
  /** Verilen rol'e ait tüm parçaları parlat, diğerlerini eski haline döndür. */
  private vurgula(rol: string | null): void {
    const g = this.grup; if (!g) return; const ag: any = (this.sahne as any)?.anaGosterim;
    g.traverse((o: any) => {
      const r = o.userData?.['rol'] || (o.userData?.['offset'] ? 'off' : null); if (!r || !o.material) return;
      const mat = o.material; if (!mat.color) return;
      if (!o.userData['_bC']) { o.userData['_bC'] = mat.color.clone(); o.userData['_bO'] = mat.opacity; }
      if (r === rol) { mat.color.copy(o.userData['_bC']).lerp(this._ak, 0.55); mat.opacity = Math.min(1, (o.userData['_bO'] ?? 1) + 0.45); }
      else { mat.color.copy(o.userData['_bC']); mat.opacity = o.userData['_bO'] ?? mat.opacity; }
    });
    if (ag?.ciz) ag.ciz();
  }

  protected override temizle(): void {
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && this.downH) cv.removeEventListener('pointerdown', this.downH, true);
    if (cv && this.dblH) cv.removeEventListener('dblclick', this.dblH);
    if (cv && this.hoverH) cv.removeEventListener('pointermove', this.hoverH);
    this.downH = this.dblH = this.hoverH = null;
  }
}
